import { ref } from 'vue';

export type NotificationType = 'success' | 'warning' | 'error';

interface NotificationOptions {
  type?: NotificationType;
  duration?: number;
}

export interface SnackBarNotification {
  id: number;
  message: string;
  type?: NotificationType;
  actionLabel?: string;
  onAction?: () => void;
  dismissible: boolean;
  remainingMs: number;
  startedAt?: number;
  timeout?: ReturnType<typeof setTimeout>;
}

export const snackBarNotifications = ref<SnackBarNotification[]>([]);

let nextSnackBarNotificationId = 1;

const durationDefaults: Record<NotificationType, number> = {
  success: 8000,
  warning: 12000,
  error: 15000,
};

export function dismissSnackBarNotification(id: number) {
  const notification = snackBarNotifications.value.find((item) => item.id === id);
  if (notification?.timeout) clearTimeout(notification.timeout);
  snackBarNotifications.value = snackBarNotifications.value.filter((item) => item.id !== id);
}

function resumeSnackBarNotification(notification: SnackBarNotification) {
  notification.startedAt = Date.now();
  notification.timeout = setTimeout(() => dismissSnackBarNotification(notification.id), notification.remainingMs);
}

function showSnackBar(
  message: string,
  {
    type,
    duration,
    actionLabel,
    onAction,
    dismissible = true,
  }: NotificationOptions & { actionLabel?: string; onAction?: () => void; dismissible?: boolean } = {}
): { dismiss: () => void } {
  const notification: SnackBarNotification = {
    id: nextSnackBarNotificationId++,
    message,
    type,
    actionLabel,
    onAction,
    dismissible,
    remainingMs: duration ?? (type ? durationDefaults[type] : 4000),
  };

  snackBarNotifications.value.push(notification);
  resumeSnackBarNotification(notification);

  return { dismiss: () => dismissSnackBarNotification(notification.id) };
}

export function pauseSnackBarNotification(notification: SnackBarNotification) {
  if (!notification.startedAt || notification.timeout === undefined) return;
  clearTimeout(notification.timeout);
  notification.timeout = undefined;
  notification.remainingMs -= Date.now() - notification.startedAt;
  notification.startedAt = undefined;
}

export function resumePausedSnackBarNotification(notification: SnackBarNotification) {
  if (notification.startedAt) return;
  resumeSnackBarNotification(notification);
}

export function showSnackBarNotification(message: string, options: NotificationOptions = {}) {
  return showSnackBar(message, options);
}

/**
 * A snack bar with an Undo action.
 *
 * The whole bar is the button: it is not dismissible, so a click on it has no other
 * meaning, and the toaster only reports clicks on itself rather than on the anchor —
 * hit-testing the anchor made the action miss depending on where it landed.
 *
 * `type` is deliberately not accepted: it would overwrite the duration, and the undo
 * window has to outlive the write it is offering to reverse.
 */
export function showUndoNotification(
  message: string,
  { onUndo, durationMs = 12000 }: { onUndo: () => void; durationMs?: number }
): { dismiss: () => void } {
  return showActionNotification(message, { actionLabel: 'Undo', onAction: onUndo, durationMs });
}

/** The same one-action snack bar under a label of your choosing. */
export function showActionNotification(
  message: string,
  { actionLabel, onAction, durationMs = 12000 }: { actionLabel: string; onAction: () => void; durationMs?: number }
): { dismiss: () => void } {
  return showSnackBar(message, { actionLabel, onAction, duration: durationMs, dismissible: false });
}
